/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { Pencil, Power, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import CouponModal from "../../../components/AdminSideComponents/coupon/CouponModal";
import { userConfirmAction, userError, userSuccess } from "../../../utils/toastUtils";

const AdminCoupons = () => {
  const API = process.env.REACT_APP_API_URL;

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/coupons?admin=true`);
      setCoupons(res.data);
    } catch (err) {
      console.error("Failed to load coupons", err);
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleDelete = async (id) => {
    const confirmed = await userConfirmAction({
      title: "Delete Coupon?",
      text: "This action cannot be undone.",
      confirmText: "Yes, Delete",
    });

    if (!confirmed) return;

    try {
      await axios.delete(`${API}/coupons/${id}`);
      userSuccess("Coupon deleted successfully");
      fetchCoupons();
    } catch (err) {
      userError("Failed to delete coupon");
    }
  };

  const handleToggle = async (id, isActive) => {
    const confirmed = await userConfirmAction({
      title: isActive ? "Deactivate Coupon?" : "Activate Coupon?",
      text: isActive
        ? "Users will no longer be able to use this coupon."
        : "This coupon will become available to users.",
      confirmText: isActive ? "Deactivate" : "Activate",
    });

    if (!confirmed) return;

    try {
      await axios.patch(`${API}/coupons/${id}/toggle`);
      userSuccess(
        isActive ? "Coupon deactivated" : "Coupon activated"
      );
      fetchCoupons();
    } catch (err) {
      userError("Failed to update coupon status");
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#FFF7F3] to-gray-50 min-h-screen">
      <div className="px-6 py-2">
        <Breadcrumb title1="Coupons" title2="Manage promotional campaigns" />
      </div>


      <div className="px-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-primaryTextColor">
            All Coupons
          </h2>

          <button
            onClick={() => {
              setEditingCoupon(null);
              setModalOpen(true);
            }}
            className="px-5 py-2 rounded-md bg-primaryBtnColor text-white font-semibold hover:bg-primaryBtnHoverColor"
          >
            + Add Coupon
          </button>
        </div>

        {/* Table */}
        <div className="rounded-2xl shadow-sm border border-[#F7BFAD] bg-white overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-[#F7BFAD]/40">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Value</th>
                <th className="px-4 py-3 text-left">Min</th>
                <th className="px-4 py-3 text-left">Usage</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>

            {loading ? (
              <tbody>
                {Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="animate-pulse border-t">
                    <td colSpan="7" className="px-4 py-4">
                      <div className="h-4 bg-gray-200 rounded" />
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                {coupons?.map((c) => (
                  <tr
                    key={c._id}
                    className="border-t hover:bg-[#FFF7F3]"
                  >
                    <td className="px-4 py-3 font-semibold text-[#f0653a]">
                      {c.code}
                    </td>
                    <td className="px-4 py-3">{c.discountType}</td>
                    <td className="px-4 py-3">
                      {c.discountType === "FLAT"
                        ? `₹${c.discountValue}`
                        : `${c.discountValue}%`}
                    </td>
                    <td className="px-4 py-3">₹{c.minOrderAmount}</td>
                    <td className="px-4 py-3">
                      {c.usedCount || 0}/{c.usageLimit || "∞"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-3 py-1 text-xs rounded-full ${c.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-200 text-gray-500"
                          }`}
                      >
                        {c.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-4">

                        {/* Edit */}
                        <button
                          onClick={() => {
                            setEditingCoupon(c);
                            setModalOpen(true);
                          }}
                          title="Edit Coupon"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-[#f0653a]/40 text-[#f0653a] hover:bg-[#FFF7F3] hover:border-[#f0653a] transition"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>

                        {/* Toggle */}
                        <button
                          onClick={() => handleToggle(c._id, c.isActive)} title={c.isActive ? "Deactivate" : "Activate"}
                          className={`inline-flex items-center justify-center h-8 w-8 rounded-md border transition
        ${c.isActive
                              ? "border-blue-400 text-blue-600 hover:bg-blue-50"
                              : "border-gray-300 text-gray-500 hover:bg-gray-100"
                            }`}
                        >
                          <Power className="h-4 w-4" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(c._id)}
                          title="Delete Coupon"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-red-400 text-red-500 hover:bg-red-50 hover:border-red-500 transition"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>

                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <CouponModal
          onClose={() => setModalOpen(false)}
          editingCoupon={editingCoupon}
          fetchCoupons={fetchCoupons}
        />
      )}
    </div>
  );
};


export default AdminCoupons;